import { useState, useCallback } from 'react';
import { invokeApig } from '../libs/awsLib';

/**
 * Hook para gestionar guardado, carga, listado y eliminación de diseños de páginas.
 */
export const useDesignManager = (designId) => {
  const [isSaving,   setIsSaving]   = useState(false);
  const [isLoading,  setIsLoading]  = useState(false);
  const [isListing,  setIsListing]  = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lastSaved,  setLastSaved]  = useState(null);
  const [error,      setError]      = useState(null);

  // -------------------------------------------------------------------------
  // listDesigns — POST /site/design/list
  // Retorna el resumen de todas las páginas del sitio (sin blob de diseño).
  // -------------------------------------------------------------------------
  const listDesigns = useCallback(async () => {
    try {
      setIsListing(true);
      setError(null);

      const response = await invokeApig({
        path:   '/site/design/list',
        method: 'POST',
        body:   { designId },
      });

      if (response.Status === 0 || response.statusCode === 200) {
        return { success: true, pages: response.pages || [], total: response.total || 0 };
      } else {
        throw new Error(response.message || 'Error al listar diseños');
      }
    } catch (err) {
      console.error('Error listing designs:', err);
      setError(err.message);
      return { success: false, error: err.message, pages: [] };
    } finally {
      setIsListing(false);
    }
  }, [designId]);

  // -------------------------------------------------------------------------
  // loadDesign — POST /site/design/find  (carga el diseño activo o el más
  //              reciente cuando no se especifica pageId)
  // -------------------------------------------------------------------------
  const loadDesign = useCallback(async (pageId = null) => {
    try {
      setIsLoading(true);
      setError(null);

      const body = { designId };
      if (pageId) body.pageId = pageId;

      const response = await invokeApig({
        path:   '/site/design/find',
        method: 'POST',
        body,
      });

      if (response.Status === 0 || response.statusCode === 200) {
        return {
          success:  true,
          design:   response.design,
          metadata: response.metadata,
          pageId:   response.pageId,
          pageInfo: response.pageInfo,
        };
      } else {
        throw new Error(response.message || 'Error al cargar el diseño');
      }
    } catch (err) {
      console.error('Error loading design:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [designId]);

  // -------------------------------------------------------------------------
  // saveDesign — POST /site/design
  // -------------------------------------------------------------------------
  const saveDesign = useCallback(async (designData, metadata = {}, pageId = null, title = '', pageType = 'static', contentType = null, linkedPageId = null) => {
    try {
      setIsSaving(true);
      setError(null);

      const body = {
        designId,
        design: designData,
        title,
        pageType,
        metadata: {
          ...metadata,
          title,
          savedAt:        new Date().toISOString(),
          componentCount: Object.keys(designData?.nodes || {}).length,
        },
      };
      if (pageId)       body.pageId       = pageId;
      if (contentType)  body.contentType  = contentType;
      if (linkedPageId) body.linkedPageId = linkedPageId;

      const response = await invokeApig({
        path:   '/site/design',
        method: 'POST',
        body,
      });

      if (response.Status === 0 || response.statusCode === 200) {
        setLastSaved(new Date());
        return { success: true, data: response };
      } else {
        throw new Error(response.message || 'Error al guardar el diseño');
      }
    } catch (err) {
      console.error('Error saving design:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsSaving(false);
    }
  }, [designId]);

  // -------------------------------------------------------------------------
  // deleteDesign — DELETE /site/design
  // Elimina permanentemente una página de diseño por su pageId.
  // -------------------------------------------------------------------------
  const deleteDesign = useCallback(async (pageId) => {
    if (!pageId) return { success: false, error: 'pageId es requerido' };
    try {
      setIsDeleting(true);
      setError(null);

      const response = await invokeApig({
        path:   '/site/design',
        method: 'DELETE',
        body:   { designId, pageId },
      });

      if (response.Status === 0 || response.statusCode === 200) {
        return { success: true };
      } else {
        throw new Error(response.message || 'Error al eliminar el diseño');
      }
    } catch (err) {
      console.error('Error deleting design:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsDeleting(false);
    }
  }, [designId]);

  // -------------------------------------------------------------------------
  // clearError
  // -------------------------------------------------------------------------
  const clearError = useCallback(() => setError(null), []);

  return {
    saveDesign,
    loadDesign,
    listDesigns,
    deleteDesign,
    isSaving,
    isLoading,
    isListing,
    isDeleting,
    lastSaved,
    error,
    clearError,
  };
};